import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, InputBase, CircularProgress, IconButton } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const MAYOTTE_BOUNDS = {
  north: -12.6, south: -13.1, east: 45.35, west: 44.9,
};

// Charge le script Google Maps une seule fois
let googleMapsLoaded = false;
let googleMapsLoading = false;
const googleMapsCallbacks = [];

function loadGoogleMaps(callback) {
  if (window.google?.maps?.places) { callback(); return; }
  googleMapsCallbacks.push(callback);
  if (googleMapsLoading) return;
  googleMapsLoading = true;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?v=3.54&key=${GOOGLE_KEY}&libraries=places&language=fr`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    googleMapsLoaded = true;
    googleMapsLoading = false;
    googleMapsCallbacks.forEach(cb => cb());
    googleMapsCallbacks.length = 0;
  };
  document.head.appendChild(script);
}

const MapboxAddressInput = ({
  label,
  placeholder = 'Rechercher une adresse...',
  value = '',
  onChange,
  onSelect,
  icon,
  iconColor = '#552583',
  error,
  disabled = false,
  showGeolocate = false,
  sx = {},
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.places);
  const containerRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    if (mapsReady) return;
    loadGoogleMaps(() => {
      setMapsReady(true);
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      const dummyDiv = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    });
  }, [mapsReady]);

  useEffect(() => {
    if (!mapsReady || autocompleteService.current) return;
    autocompleteService.current = new window.google.maps.places.AutocompleteService();
    const dummyDiv = document.createElement('div');
    placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
    sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [mapsReady]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((text) => {
    if (!text || text.length < 2 || !autocompleteService.current) { setSuggestions([]); return; }
    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input: text,
        sessionToken: sessionToken.current,
        locationBias: new window.google.maps.LatLngBounds(
          { lat: MAYOTTE_BOUNDS.south, lng: MAYOTTE_BOUNDS.west },
          { lat: MAYOTTE_BOUNDS.north, lng: MAYOTTE_BOUNDS.east }
        ),
        language: 'fr',
      },
      (predictions, status) => {
        setLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, []);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange?.(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(text), 280);
  };

  const handleSelect = (prediction) => {
    setQuery(prediction.description);
    setSuggestions([]);
    setFocused(false);

    // Récupérer les détails (coordonnées GPS) via Place Details
    if (!placesService.current) {
      onSelect?.({ address: prediction.description, fullAddress: prediction.description, place_name: prediction.description });
      return;
    }
    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry', 'address_components', 'formatted_address', 'name'],
        sessionToken: sessionToken.current,
      },
      (place, status) => {
        // Renouveler le session token après chaque sélection
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          onSelect?.({ address: prediction.description, fullAddress: prediction.description, place_name: prediction.description });
          return;
        }
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        const components = place.address_components || [];
        const get = (type) => components.find(c => c.types.includes(type))?.long_name || '';
        const result = {
          address: place.formatted_address || prediction.description,
          fullAddress: place.formatted_address || prediction.description,
          shortAddress: place.name || prediction.structured_formatting?.main_text || '',
          place_name: place.formatted_address || prediction.description,
          street: `${get('street_number')} ${get('route')}`.trim(),
          housenumber: get('street_number'),
          city: get('locality') || get('administrative_area_level_2') || get('administrative_area_level_1'),
          postal_code: get('postal_code'),
          latitude: lat,
          longitude: lng,
        };
        onSelect?.(result);
      }
    );
  };

  const handleGeolocate = () => {
    setGeoLoading(true);
    const reverseGeocode = (lat, lng) => {
      if (!mapsReady || !window.google?.maps) {
        onSelect?.({ address: '', latitude: lat, longitude: lng });
        setGeoLoading(false);
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng }, language: 'fr' }, (results, status) => {
        setGeoLoading(false);
        if (status === 'OK' && results[0]) {
          const place = results[0];
          const components = place.address_components || [];
          const get = (type) => components.find(c => c.types.includes(type))?.long_name || '';
          setQuery(place.formatted_address);
          onSelect?.({
            address: place.formatted_address,
            fullAddress: place.formatted_address,
            place_name: place.formatted_address,
            street: `${get('street_number')} ${get('route')}`.trim(),
            city: get('locality') || get('administrative_area_level_2'),
            postal_code: get('postal_code'),
            latitude: lat,
            longitude: lng,
          });
        } else {
          setQuery('Position détectée — précisez votre adresse');
          onSelect?.({ address: '', latitude: lat, longitude: lng });
        }
      });
    };

    if (!navigator.geolocation) {
      setGeoLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => reverseGeocode(coords.latitude, coords.longitude),
      () => { setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    onChange?.('');
    onSelect?.(null);
  };

  const showSuggestions = focused && suggestions.length > 0;

  return (
    <Box ref={containerRef} sx={{ position: 'relative', ...sx }}>
      {label && (
        <Typography sx={{ color: 'rgba(0,0,0,0.5)', mb: 0.8, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
          {label}
        </Typography>
      )}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        background: 'rgba(85,37,131,0.04)',
        border: error ? '1.5px solid #e74c3c' : focused ? '1.5px solid rgba(85,37,131,0.5)' : '1.5px solid rgba(85,37,131,0.12)',
        borderRadius: '14px', px: 1.5, py: 0.5,
        transition: 'all 0.3s ease',
        boxShadow: focused ? '0 0 20px rgba(85,37,131,0.1)' : 'none',
        '&:hover': { background: 'rgba(85,37,131,0.07)', borderColor: 'rgba(85,37,131,0.25)' },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: iconColor, minWidth: 28 }}>
          {icon || <SearchIcon sx={{ fontSize: 20, opacity: 0.6 }} />}
        </Box>
        <InputBase
          fullWidth
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setFocused(true)}
          disabled={disabled}
          sx={{ py: 1, fontSize: '0.95rem', color: '#1a1a1a', '& input::placeholder': { color: 'rgba(0,0,0,0.3)', opacity: 1 } }}
        />
        {loading && <CircularProgress size={18} sx={{ color: '#552583', flexShrink: 0 }} />}
        {query && !loading && (
          <IconButton size="small" onClick={handleClear} sx={{ color: 'rgba(0,0,0,0.3)', '&:hover': { color: '#552583' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        {showGeolocate && (
          <IconButton size="small" onClick={handleGeolocate} disabled={geoLoading}
            sx={{ color: '#552583', opacity: geoLoading ? 0.5 : 1, '&:hover': { background: 'rgba(85,37,131,0.08)' } }}>
            {geoLoading ? <CircularProgress size={18} sx={{ color: '#552583' }} /> : <MyLocationIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        )}
      </Box>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <Box sx={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1200,
          mt: 0.5, bgcolor: '#fff', borderRadius: '14px',
          border: '1px solid rgba(85,37,131,0.12)',
          boxShadow: '0 12px 40px rgba(85,37,131,0.15)',
          overflow: 'hidden', maxHeight: 320, overflowY: 'auto',
        }}>
          {suggestions.map((pred, i) => {
            const main = pred.structured_formatting?.main_text || pred.description;
            const secondary = pred.structured_formatting?.secondary_text || '';
            return (
              <Box
                key={pred.place_id}
                onClick={() => handleSelect(pred)}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  px: 2, py: 1.5, cursor: 'pointer',
                  borderBottom: i < suggestions.length - 1 ? '1px solid rgba(85,37,131,0.06)' : 'none',
                  transition: 'background 0.15s',
                  '&:hover': { background: 'rgba(85,37,131,0.04)' },
                }}
              >
                <PlaceIcon sx={{ color: '#552583', fontSize: 20, mt: 0.3, flexShrink: 0, opacity: 0.7 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {main}
                  </Typography>
                  {secondary && (
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.45)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {secondary}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          <Box sx={{ px: 2, py: 0.8, borderTop: '1px solid rgba(85,37,131,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <img src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" alt="Powered by Google" style={{ height: 14 }} />
          </Box>
        </Box>
      )}

      {error && (
        <Typography sx={{ color: '#e74c3c', fontSize: '0.75rem', mt: 0.5, ml: 0.5 }}>{error}</Typography>
      )}
    </Box>
  );
};

export default MapboxAddressInput;
